import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import {
  useTaskNotificationActions,
  useTaskNotificationsList,
} from '../../hooks/notifications/useTaskNotificationActions';

interface Props {
  onItemSelected: () => void;
}

function kindGlyph(kind: string): string {
  switch (kind) {
    case 'ASSIGNED':
      return '➤';
    case 'MENTIONED':
      return '@';
    case 'STATUS':
      return '✓';
    case 'DUE_SOON':
      return '⏰';
    case 'OVERDUE':
      return '!';
    case 'COMMENT':
      return '💬';
    case 'BLOCKED':
      return '⛔';
    default:
      return '•';
  }
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

export default function TaskNotificationsPanel({ onItemSelected }: Props) {
  const allItems = useTaskNotificationsList();
  const items = allItems.filter((i) => !i.is_read);
  const { markOneRead, markEveryRead } = useTaskNotificationActions();
  const navigate = useNavigate();

  const hasItems = items.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {!hasItems && (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--gt-text-muted, #6b7280)',
          }}
        >
          You're all caught up.
        </div>
      )}
      {hasItems && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1 }}>
          {items.map((it) => (
            <li
              key={it.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 6,
                padding: '0 6px 0 0',
                background: 'var(--gt-accent-soft, #f0f9f4)',
                borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.04))',
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (it.task_id) {
                    navigate(`/tasks/${encodeURIComponent(it.task_id)}`);
                  }
                  void markOneRead(it.id);
                  onItemSelected();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  flex: 1,
                  minWidth: 0,
                  padding: '10px 8px 10px 14px',
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--gt-accent, #1f4d3f)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {kindGlyph(it.kind)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {it.title}
                  </div>
                  {it.body && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--gt-text-muted, #6b7280)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {it.body}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--gt-text-muted, #9ca3af)',
                    flexShrink: 0,
                    paddingTop: 2,
                  }}
                >
                  {relativeTime(it.created_at)}
                </div>
              </button>
              <button
                type="button"
                aria-label="Mark as read"
                title="Mark as read"
                onClick={(e) => {
                  e.stopPropagation();
                  void markOneRead(it.id);
                }}
                style={{
                  alignSelf: 'center',
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  border: '1px solid var(--gt-divider, rgba(0,0,0,.08))',
                  background: 'var(--gt-surface, #fff)',
                  color: 'var(--gt-accent, #1f4d3f)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Check style={{ width: 14, height: 14 }} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {hasItems && (
        <div
          style={{
            padding: '8px 14px',
            borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={() => void markEveryRead()}
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 12,
              color: 'var(--gt-accent, #1f4d3f)',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Mark all read
          </button>
        </div>
      )}
    </div>
  );
}
